import { partition } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBSeenByList } from '@shared/scenes/chat/thread/components/seenBy/list';
import { useThreadContext } from '@shared/scenes/chat/thread/containers/thread/provider';
import { API } from '@shared/types/api';
import { useChannelId, useChannelUsers, useCurrentUserId } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  users: API.ShortUserFragment[];
  sender?: API.ShortUserFragment;
  onClose(): void;
  anchorName: string;
  align: 'right' | 'left';
}

export function MBSeenBySheet({ users, sender, onClose, anchorName, align }: Props) {
  const [keyword, setKeyword] = useState<string>('');
  const { channelSlug } = useThreadContext();
  const channelId = useChannelId(channelSlug);
  const { users: channelUsers } = useChannelUsers({ channelId });
  const currentUserId = useCurrentUserId();

  const [, notSeen] = useMemo(
    () =>
      partition(
        channelUsers.filter(user => user.id !== currentUserId && user.id !== sender?.id),
        user => users.some(u => u.id === user.id),
      ),
    [channelUsers, currentUserId, users, sender],
  );

  const renderTitleBar = useCallback(() => {
    return (
      <View paddingHorizontal={1} paddingTop={2}>
        <MBSearchBar
          value={keyword}
          onChangeText={setKeyword}
          placeholder={i18n('common.search')}
          returnKeyType="search"
          autoFocus={true}
        />
      </View>
    );
  }, [keyword]);

  return (
    <AnchoredSheet
      onClose={onClose}
      renderTitleBar={renderTitleBar}
      anchorName={anchorName}
      align={align === 'right' ? 'left' : 'right'}
      style={{ maxHeight: '60vh' }}
    >
      <MBSeenByList seen={users} notSeen={notSeen} keyword={keyword} />
    </AnchoredSheet>
  );
}
